// src/actions/user.actions.js
/* global process */

import {
  BrowserUtils,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import config from "config";

import {
  emailLoginRequest,
  loginRequest,
  signUpRequest,
  SILENT_REDIRECT_URI,
  silentEmailLoginRequest,
  silentLoginRequest,
} from "../authConfig";
import {
  alertConstants,
  salesOrderConstants,
  userConstants,
} from "../constants";
import {
  clearAuthRedirectPending,
  isAuthRedirectPending,
  setAuthRedirectPending,
} from "../helpers/authRedirectPending";
import { setSessionClientId } from "../helpers/client";
import { clearMsalClientSession } from "../helpers/msalSession";
import { clearAuthRelatedSessionStorage } from "../helpers/postAuthGrace";
import msalInstance, { msalReady } from "../msalInstance";
import { userService } from "../services";
import { store } from "../store";
import { alertActions } from "./alert.actions";

const isMsalInteractionRequiredError = (error) => {
  if (!error) return false;
  if (error instanceof InteractionRequiredAuthError) return true;
  const blob = `${error.errorCode || ""} ${error.errorMessage || ""} ${
    error.subError || ""
  } ${error.message || ""}`;
  return /interaction_required|AADB2C90077/i.test(blob);
};

/** MSAL blocks nested interactive APIs while a redirect/sign-out is still tearing down. */
const isMsalInteractionInProgressError = (error) => {
  if (!error) return false;
  const code = error.errorCode || "";
  if (code === "interaction_in_progress") return true;
  return /interaction_in_progress/i.test(String(error.message || ""));
};

const AUTH_REDIRECT_ALREADY_IN_PROGRESS = new Error(
  "Sign-in is already in progress. Please wait or refresh the page.",
);

/** Shared by popup and redirect paths (Cypress runs the app in an iframe → popup). */
const rejectIfAuthRedirectPending = (dispatch, failureAction) => {
  if (!isAuthRedirectPending()) {
    return false;
  }
  dispatch(failureAction(AUTH_REDIRECT_ALREADY_IN_PROGRESS));
  return true;
};

const redirectTopWindowB2cFrontChannelLogout = () => {
  const port = window.location.port || "";
  const portPart = port ? `:${port}` : "";
  const origin = `${window.location.protocol}//${window.location.hostname}${portPart}`;
  try {
    const baseAuthority = (
      (config.emailMsalConfig && config.emailMsalConfig.authority) ||
      config.adsMsalConfig.authority ||
      ""
    ).replace(/\/$/, "");
    const logoutUrl = `${baseAuthority}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
      `${origin}/#/login`,
    )}`;
    window.location.href = logoutUrl;
  } catch (_e) {
    window.location.href = `${origin}/#/login`;
  }
};

/**
 * Avoid BrowserAuthError: interaction_in_progress when logoutRedirect runs
 * during MSAL event handling (e.g. HANDLE_REDIRECT_END) or overlapping work.
 * Defer to a new task, retry with backoff, then IdP front-channel logout.
 * Related: Sentry ADSWEBUI-REACT-2ET
 */
const scheduleMsalLogoutRedirect = (logoutRequest) => {
  const backoffMs = [0, 15, 40, 100, 250, 500];

  const attempt = (index) => {
    const delay = backoffMs[Math.min(index, backoffMs.length - 1)];
    setTimeout(() => {
      const retryOrFallback = () => {
        if (index + 1 < backoffMs.length) {
          attempt(index + 1);
        } else {
          redirectTopWindowB2cFrontChannelLogout();
        }
      };
      try {
        const maybePromise = msalInstance.logoutRedirect(logoutRequest);
        if (maybePromise && typeof maybePromise.then === "function") {
          maybePromise.catch((err) => {
            if (isMsalInteractionInProgressError(err)) {
              retryOrFallback();
            } else {
              console.error("logoutRedirect rejected", err);
            }
          });
        }
      } catch (err) {
        if (isMsalInteractionInProgressError(err)) {
          retryOrFallback();
        } else {
          console.error("logoutRedirect threw", err);
        }
      }
    }, delay);
  };

  attempt(0);
};

/**
 * Defer / retry loginRedirect when MSAL still has another interaction
 * (e.g. overlapping ssoSilent from startup or Login auto-probe).
 */
const scheduleMsalLoginRedirect = (
  instance,
  request,
  onExhausted,
  onFatal,
  onSuccess,
) => {
  const backoffMs = [0, 20, 60, 150, 400];
  const attempt = (index) => {
    const delay = backoffMs[Math.min(index, backoffMs.length - 1)];
    setTimeout(() => {
      const retry = () => {
        if (index + 1 < backoffMs.length) {
          attempt(index + 1);
        } else {
          onExhausted();
        }
      };
      try {
        const maybePromise = instance.loginRedirect(request);
        if (maybePromise && typeof maybePromise.then === "function") {
          maybePromise.then(
            (value) => {
              if (typeof onSuccess === "function") {
                onSuccess(value);
              }
            },
            (err) => {
              if (isMsalInteractionInProgressError(err)) {
                retry();
              } else {
                onFatal(err);
              }
            },
          );
        } else if (typeof onSuccess === "function") {
          onSuccess();
        }
      } catch (err) {
        if (isMsalInteractionInProgressError(err)) {
          retry();
        } else {
          onFatal(err);
        }
      }
    }, delay);
  };
  attempt(0);
};

const loginRedirectWhenIdle = (instance, request) =>
  new Promise((resolve, reject) => {
    scheduleMsalLoginRedirect(
      instance,
      request,
      () =>
        reject(
          new Error(
            "Unable to start sign-in while another authentication step is finishing.",
          ),
        ),
      reject,
      resolve,
    );
  });

const clearStaleLoginHintsAfterSilentFailure = () => {
  try {
    localStorage.removeItem("account");
  } catch (_e) {
    // ignore
  }
  try {
    localStorage.removeItem("ads.loginHint");
  } catch (_e2) {
    // ignore
  }
};

// Ensure MSAL is initialized once and awaited by consumers via msalReady

function errorStatus(err) {
  return (dispatch) => {
    console.log("ERROR", err);
    dispatch(alertActions.error(err.toString()));
  };
}

function maintenanceMessage(type, err) {
  return (dispatch) => {
    dispatch({ type: type, err });
  };
}

function logout() {
  clearAuthRedirectPending();
  clearAuthRelatedSessionStorage();
  setSessionClientId();
  userService.logout();
  // Drop cached MSAL accounts/tokens before IdP redirect so the next login
  // cannot finalize against getAllAccounts()[0] from the previous user.
  void clearMsalClientSession(msalInstance);

  store.dispatch({ type: userConstants.LOGOUT });
  console.info(
    "window.parent !== window: ",
    window.parent !== window,
    window,
    window.parent,
  );

  // IFRAME-SAFE LOGOUT
  // Rationale: MSAL blocks interactive API calls inside iframes during/after an
  // auth response to prevent reload loops (BrowserAuthError: block_iframe_reload).
  // Instead of calling popup/redirect from the frame, break out to the TOP window
  // and use Azure B2C front‑channel logout. This ensures the IdP session is truly
  // terminated and we return to our login route.
  // Related: Sentry ADSWEBUI-REACT-2A0
  if (window.parent !== window) {
    const port = window.location.port || "";
    const portPart = port ? `:${port}` : "";
    const origin = `${window.location.protocol}//${window.location.hostname}${portPart}`;
    try {
      const baseAuthority = (
        (config.emailMsalConfig && config.emailMsalConfig.authority) ||
        config.adsMsalConfig.authority ||
        ""
      ).replace(/\/$/, "");
      const logoutUrl = `${baseAuthority}/oauth2/v2.0/logout?post_logout_redirect_uri=${encodeURIComponent(
        `${origin}/#/login`,
      )}`;
      if (window.top && window.top !== window) {
        window.top.location.href = logoutUrl;
      } else {
        window.location.href = logoutUrl;
      }
    } catch (_e) {
      // Fallback: best‑effort return to app login if we cannot compute/logout at IdP
      window.location.href = `${origin}/#/login`;
    }
  } else {
    // NORMAL (TOP-WINDOW) LOGOUT
    // Safe to use MSAL redirect API when not framed. We explicitly set the
    // postLogoutRedirectUri back to our login screen.
    const port = window.location.port || "";
    const portPart = port ? `:${port}` : "";
    const logoutRequest = {
      account: msalInstance.getActiveAccount(),
      postLogoutRedirectUri: `${window.location.protocol}//${window.location.hostname}${portPart}/#/login`,
      onRedirectNavigate: () => !BrowserUtils.isInIframe(),
    };
    scheduleMsalLogoutRedirect(logoutRequest);
  }

  return { type: userConstants.LOGOUT };
}

function msalLogin(instance) {
  return (dispatch) => {
    dispatch(request(userConstants.MSAL_LOGIN_REQUEST));

    const performLogin = async () => {
      await msalReady;

      console.info(
        "window.parent !== window: ",
        window.parent !== window,
        window,
        window.parent,
      );

      const handleLoginError = (error, loginFn) => {
        console.error("Login error:", error);
        // Continue with login even if SSO probe fails
        if (
          error.errorCode === "unauthorized_client" ||
          error.errorCode === "interaction_required" ||
          error instanceof InteractionRequiredAuthError
        ) {
          console.info(
            "SSO probe failed or interaction required, continuing with full login flow",
          );
          return loginFn(loginRequest);
        }
        throw error;
      };

      if (rejectIfAuthRedirectPending(dispatch, failure)) {
        return;
      }

      if (window.parent !== window) {
        try {
          const response = await instance.loginPopup(loginRequest);
          dispatch(success(response.account));
        } catch (error) {
          await handleLoginError(error, (req) => instance.loginPopup(req))
            .then((response) => dispatch(success(response.account)))
            .catch((error) => dispatch(failure(error)));
        }
      } else {
        try {
          setAuthRedirectPending();
          await new Promise((r) => setTimeout(r, 0));
          const response = await loginRedirectWhenIdle(
            instance,
            loginRequest,
          );
          dispatch(success(response.account));
        } catch (error) {
          await handleLoginError(error, (req) => {
            setAuthRedirectPending();
            return loginRedirectWhenIdle(instance, req);
          })
            .then((response) => dispatch(success(response.account)))
            .catch((err) => {
              clearAuthRedirectPending();
              dispatch(failure(err));
            });
        }
      }
    };

    performLogin().catch((error) => {
      clearAuthRedirectPending();
      dispatch(failure(error));
    });
  };

  function request(msalLogin) {
    return { type: userConstants.MSAL_LOGIN_REQUEST, msalLogin };
  }
  function success(msalUser) {
    return { type: userConstants.MSAL_LOGIN_SUCCESS, msalUser };
  }
  function failure(error) {
    return { type: userConstants.MSAL_LOGIN_FAILURE, error };
  }
}

function silentLogin(instance) {
  return (dispatch) => {
    msalReady.then(() => {
      if (isAuthRedirectPending()) {
        return;
      }
      dispatch(request(userConstants.MSAL_LOGIN_REQUEST));
      console.info(
        "window.parent !== window: ",
        window.parent !== window,
        window,
        window.parent,
      );

      instance
        .ssoSilent({
          ...silentLoginRequest,
          redirectUri: SILENT_REDIRECT_URI,
        })
        .catch((error) => {
          // Unhandled rejections surface as CRA/runtime overlay; B2C returns
          // interaction_required when there is no iframe/session for prompt=none.
          if (isMsalInteractionRequiredError(error)) {
            clearStaleLoginHintsAfterSilentFailure();
          }
          dispatch(failure(error));
        });
    });
  };

  function request(msalLogin) {
    return { type: userConstants.MSAL_LOGIN_REQUEST, msalLogin };
  }
  function failure(error) {
    return { type: userConstants.MSAL_LOGIN_FAILURE, error };
  }
}

function silentLoginEmail(instance) {
  return (dispatch) => {
    msalReady.then(() => {
      if (isAuthRedirectPending()) {
        return;
      }
      dispatch(request(userConstants.EMAIL_LOGIN_REQUEST));
      console.info(
        "window.parent !== window: ",
        window.parent !== window,
        window,
        window.parent,
      );

      instance
        .ssoSilent({
          ...silentEmailLoginRequest,
          redirectUri: SILENT_REDIRECT_URI,
        })
        .catch((error) => {
          if (isMsalInteractionRequiredError(error)) {
            clearStaleLoginHintsAfterSilentFailure();
          }
          dispatch(failure(error));
        });
    });
  };

  function request(msalLogin) {
    return { type: userConstants.EMAIL_LOGIN_REQUEST, msalLogin };
  }
  function failure(error) {
    return { type: userConstants.EMAIL_LOGIN_FAILURE, error };
  }
}

function emailLogin(instance) {
  return (dispatch) => {
    dispatch(request(userConstants.EMAIL_LOGIN_REQUEST));

    const performEmailLogin = () => {
      console.info(
        "window.parent !== window: ",
        window.parent !== window,
        window,
        window.parent,
      );
      if (rejectIfAuthRedirectPending(dispatch, failure)) {
        return;
      }
      if (window.parent !== window) {
        instance
          .loginPopup(emailLoginRequest)
          .then((response) => {
            dispatch(success(response.account));
          })
          .catch((error) => {
            if (error instanceof InteractionRequiredAuthError) {
              console.error(
                "Interaction required. Prompting user to log in.",
              );
              instance
                .loginPopup(loginRequest)
                .then((response) => {
                  dispatch(success(response.account));
                })
                .catch((error) => {
                  dispatch(failure(error));
                });
            } else {
              dispatch(failure(error));
            }
          });
      } else {
        setAuthRedirectPending();
        loginRedirectWhenIdle(instance, emailLoginRequest)
          .then((response) => {
            dispatch(success(response.account));
          })
          .catch((error) => {
            if (error instanceof InteractionRequiredAuthError) {
              console.error(
                "Interaction required. Redirecting user to log in.",
              );
              setAuthRedirectPending();
              loginRedirectWhenIdle(instance, loginRequest)
                .then((response) => {
                  dispatch(success(response.account));
                })
                .catch((err) => {
                  clearAuthRedirectPending();
                  dispatch(failure(err));
                });
            } else {
              clearAuthRedirectPending();
              dispatch(failure(error));
            }
          });
      }
    };

    msalReady.then(performEmailLogin).catch((error) => {
      clearAuthRedirectPending();
      dispatch(failure(error));
    });
  };

  function request(emailLogin) {
    return { type: userConstants.EMAIL_LOGIN_REQUEST, emailLogin };
  }
  function success(msalUser) {
    return { type: userConstants.EMAIL_LOGIN_SUCCESS, msalUser };
  }
  function failure(error) {
    return { type: userConstants.EMAIL_LOGIN_FAILURE, error };
  }
}

function emailSignUp(instance) {
  return async (dispatch) => {
    dispatch(request(userConstants.EMAIL_LOGIN_REQUEST));
    await msalReady;
    async function checkSignupEndpointAvailability() {
      try {
        const signupPolicyUrl = process.env.REACT_APP_SIGNUP_POLICY; ///if you put this in quotes in the .env file, it won't work

        const response = await fetch(signupPolicyUrl, {
          method: "GET",
        });

        // Return true if the endpoint is reachable (200-299), false for 404s
        return response.ok;
      } catch (error) {
        console.error("Signup endpoint check failed:", error);
        return false;
      }
    }
    // Check if signup endpoint is available before redirecting
    const signupAvailable = await checkSignupEndpointAvailability();
    if (!signupAvailable) {
      const errorMessage =
        "Account creation is temporarily unavailable. Please try again later.";
      dispatch(failure(errorMessage));
      dispatch(alertActions.error(errorMessage)); // This will show the toast
      return;
    }

    // Proceed with normal signup flow
    if (rejectIfAuthRedirectPending(dispatch, failure)) {
      return;
    }
    if (window.parent !== window) {
      instance
        .loginPopup(signUpRequest)
        .then((response) => {
          dispatch(success(response.account));
        })
        .catch((error) => {
          dispatch(failure(error));
        });
    } else {
      setAuthRedirectPending();
      loginRedirectWhenIdle(instance, signUpRequest).catch((err) => {
        clearAuthRedirectPending();
        dispatch(failure(err));
      });
    }
  };

  function request(emailSignUp) {
    return { type: userConstants.EMAIL_LOGIN_REQUEST, emailSignUp };
  }
  function success(msalUser) {
    return { type: userConstants.EMAIL_LOGIN_SUCCESS, msalUser };
  }
  function failure(error) {
    return { type: userConstants.EMAIL_LOGIN_FAILURE, error };
  }
}

function msalLogout() {
  return (dispatch) => {
    // Initiate MSAL sign-out flow and dispatch LOGOUT via the existing helper
    // Note: logout() internally calls store.dispatch and also returns an action;
    // we rely on its internal dispatch to avoid duplicate LOGOUT actions here.
    logout();
    // Ensure related slices are cleared using thunk-aware dispatch
    dispatch(clearState());
  };
}

function clearState() {
  return (dispatch) => {
    dispatch(clearAlert());
    dispatch(clearAuthUser());
    dispatch(clearStateSO());
  };
  function clearAlert() {
    return { type: alertConstants.CLEAR };
  }
  function clearAuthUser() {
    return { type: userConstants.LOGOUT };
  }
  function clearStateSO() {
    return { type: salesOrderConstants.SALES_ORDERS_FAILURE };
  }
}

// Removed unused httpErrorHandler (was referencing out-of-scope symbols and unused)

export const userActions = {
  logout,
  emailLogin,
  msalLogin,
  msalLogout,
  emailSignUp,
  errorStatus,
  silentLogin,
  silentLoginEmail,
  maintenanceMessage,
};
